/**
 * Luxury search helpers (styles in search-luxe.css). Only does work while the search panel is open.
 * - Rotating hint in the empty search box, taken from your Popular Searches
 * - "Recent searches" chips (stored on this device only) for one-tap repeat searches
 * - A short perks line under the search bar
 */
const STORAGE_KEY = 'sl-recent-searches';
const MAX_RECENT = 6;
const HINT_INTERVAL = 2600;
const PERKS = ['Lifetime warranty', 'Express U.S. shipping'];

const storage = {
  read() {
    try {
      const list = JSON.parse(localStorage.getItem(STORAGE_KEY) || '[]');
      return Array.isArray(list) ? list.filter((item) => typeof item === 'string') : [];
    } catch (error) {
      return [];
    }
  },
  write(list) {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(list.slice(0, MAX_RECENT)));
    } catch (error) {
      /* private mode or storage blocked: recent searches simply aren't kept */
    }
  },
};

function remember(term) {
  const clean = (term || '').trim();
  if (clean.length < 2) return;
  const list = storage.read().filter((item) => item.toLowerCase() !== clean.toLowerCase());
  list.unshift(clean);
  storage.write(list);
}

const clockIcon =
  '<svg viewBox="0 0 16 16" fill="none" aria-hidden="true"><circle cx="8" cy="8" r="6.2" stroke="currentColor" stroke-width="1.3"/><path d="M8 4.8V8l2.2 1.6" stroke="currentColor" stroke-width="1.3" stroke-linecap="round"/></svg>';

function init() {
  const modal = document.getElementById('search-modal');
  const dialog = modal?.querySelector('.search-modal__content');
  if (!modal || !dialog) return;

  const searchUrl = (() => {
    try {
      return Theme.routes.search_url || '/search';
    } catch (error) {
      return '/search';
    }
  })();

  let hintTimer;
  let hintIndex = 0;
  let baseHint = '';

  const input = () => modal.querySelector('.search-input');

  const renderRecent = () => {
    const discovery = modal.querySelector('.search-discovery');
    if (!discovery) return;
    discovery.querySelector('.sl-recent')?.remove();
    const terms = storage.read();
    if (!terms.length) return;

    const wrap = document.createElement('div');
    wrap.className = 'sl-recent';
    wrap.innerHTML =
      '<div class="sl-recent__head"><p class="search-discovery__label">Recent searches</p><button type="button" class="sl-recent__clear">Clear</button></div><ul class="sl-recent__list" role="list"></ul>';
    const list = wrap.querySelector('ul');
    terms.forEach((term) => {
      const li = document.createElement('li');
      const a = document.createElement('a');
      a.className = 'sl-recent__chip';
      a.href = `${searchUrl}?q=${encodeURIComponent(term)}`;
      a.innerHTML = clockIcon;
      a.append(document.createTextNode(term));
      li.append(a);
      list.append(li);
    });
    wrap.querySelector('.sl-recent__clear').addEventListener('click', () => {
      storage.write([]);
      wrap.remove();
    });
    discovery.prepend(wrap);
  };

  const addPerks = () => {
    const header = modal.querySelector('.predictive-search-form__header');
    if (!header || header.querySelector('.sl-perks')) return;
    const perks = document.createElement('p');
    perks.className = 'sl-perks';
    perks.setAttribute('aria-hidden', 'true');
    PERKS.forEach((text) => {
      const span = document.createElement('span');
      span.textContent = text;
      perks.append(span);
    });
    header.append(perks);
  };

  const hints = () =>
    Array.from(modal.querySelectorAll('.search-discovery__chip'), (chip) => chip.textContent.trim()).filter(Boolean);

  const startHints = () => {
    const field = input();
    if (!field || window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    baseHint = baseHint || field.getAttribute('placeholder') || 'Search';
    const list = hints();
    if (!list.length) return;
    clearInterval(hintTimer);
    hintTimer = setInterval(() => {
      if (field.value) return;
      field.classList.add('sl-hint-fade');
      setTimeout(() => {
        field.setAttribute('placeholder', `Try "${list[hintIndex % list.length]}"`);
        field.classList.remove('sl-hint-fade');
        hintIndex += 1;
      }, 300);
    }, HINT_INTERVAL);
  };

  const stopHints = () => {
    clearInterval(hintTimer);
    const field = input();
    if (field && baseHint) field.setAttribute('placeholder', baseHint);
  };

  const onOpen = () => {
    addPerks();
    renderRecent();
    startHints();
  };

  // React to the dialog opening and closing
  new MutationObserver(() => (dialog.open ? onOpen() : stopHints())).observe(dialog, { attributes: true, attributeFilter: ['open'] });

  // The theme re-renders the discovery area when the box is cleared; put recent searches back
  new MutationObserver(() => {
    if (dialog.open && !modal.querySelector('.sl-recent') && storage.read().length) renderRecent();
  }).observe(modal, { childList: true, subtree: true });

  // Remember what people search for: typed searches, chip taps and result taps
  modal.addEventListener('submit', () => remember(input()?.value), true);
  modal.addEventListener(
    'click',
    (event) => {
      const link = event.target instanceof Element ? event.target.closest('a') : null;
      if (!link) return;
      if (link.matches('.search-discovery__chip, .predictive-search-results__pill, .sl-recent__chip')) {
        remember(link.textContent);
      } else if (input()?.value) {
        remember(input().value);
      }
    },
    true
  );

  if (dialog.open) onOpen();
}

if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
else init();
